'use client';

import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx/lite';
import { useCallback, useMemo, useState } from 'react';
import { CaretIcon } from '~/components/Icons';
import { STATS_CONFIG } from '~/config/statsConfig';
import statsMetaData from '~/data/statsMetaData.json';
import { formatGameDate } from '~/utils/formatDate';
import { getColumnMetadata } from '~/utils/getColumnMetadata';
import { Container } from '../../Container';
import { CustomDropdown } from '../../CustomDropdown';

// Modify STATS_CONFIG for game logs to use gameDate instead of year
const GAME_LOG_STATS_CONFIG = {
  ...STATS_CONFIG,
  batting: {
    ...STATS_CONFIG.batting,
    columns: STATS_CONFIG.batting.columns
      .filter((col) => !['gamesPlayed', 'gamesStarted'].includes(col.accessor))
      .map((col) =>
        col.accessor === 'year'
          ? [
              { ...col, accessor: 'gameDate', header: 'Date' },
              { accessor: 'homeTeamName', header: 'Home Team', statTableId: 0 },
              { accessor: 'awayTeamName', header: 'Away Team', statTableId: 0 },
            ]
          : [col],
      )
      .flat(),
  },
  pitching: {
    ...STATS_CONFIG.pitching,
    columns: STATS_CONFIG.pitching.columns
      .filter((col) => !['gamesPlayed', 'gamesStarted'].includes(col.accessor))
      .map((col) =>
        col.accessor === 'year'
          ? [
              { ...col, accessor: 'gameDate', header: 'Date' },
              { accessor: 'homeTeamName', header: 'Home Team', statTableId: 0 },
              { accessor: 'awayTeamName', header: 'Away Team', statTableId: 0 },
            ]
          : [col],
      )
      .flat(),
  },
  fielding: {
    ...STATS_CONFIG.fielding,
    columns: STATS_CONFIG.fielding.columns
      .filter((col) => !['gamesPlayed', 'gamesStarted'].includes(col.accessor))
      .map((col) =>
        col.accessor === 'year'
          ? [
              { ...col, accessor: 'gameDate', header: 'Date' },
              { accessor: 'homeTeamName', header: 'Home Team', statTableId: 0 },
              { accessor: 'awayTeamName', header: 'Away Team', statTableId: 0 },
            ]
          : [col],
      )
      .flat(),
  },
};

// Memoize column creation
const columnHelper = createColumnHelper();
const createColumns = (columns, statsMetaData) => {
  // Map columns with their metadata
  const columnsWithMeta = columns.map((col) => {
    const metadata = getColumnMetadata(
      col.accessor,
      statsMetaData,
      col.statTableId,
    );

    // For special header columns like gameDate, etc.
    const isHeader = ['gameDate', 'homeTeamName', 'awayTeamName'].includes(
      col.accessor,
    );
    const specialOrder = {
      gameDate: 1,
      homeTeamName: 2,
      awayTeamName: 3,
    }[col.accessor];

    return {
      columnDef: columnHelper.accessor(col.accessor, {
        header: metadata?.headerText || col.header,
        cell: (info) => {
          const value = info.getValue();

          // Handle null/undefined values
          if (value === null || value === undefined || value === 0) {
            return <div>-</div>;
          }

          // Handle date formatting
          if (col.accessor === 'gameDate') {
            return <div>{formatGameDate(value)}</div>;
          }

          // Handle team name columns
          if (
            col.accessor === 'homeTeamName' ||
            col.accessor === 'awayTeamName'
          ) {
            return <span>{value}</span>;
          }

          // Apply precision formatting if specified in metadata
          if (metadata?.precision !== undefined && typeof value === 'number') {
            const formatted = value.toFixed(metadata.precision);
            return (
              <div>
                {metadata.leadingZeroFlg
                  ? formatted
                  : formatted.replace(/^0/, '')}
              </div>
            );
          }

          return <div>{value}</div>;
        },
        sortingFn: customSortingFn,
        enableSorting: true,
        sortDescFirst: true,
        meta: {
          ...col.meta,
          tooltip: metadata?.toolTipText,
          isPercentage: metadata?.percentFlg,
          seqNo: metadata?.seqNo || 999, // Use metadata seqNo for sorting
        },
      }),
      seqNo: metadata?.seqNo || 999, // Store seqNo for sorting columns
      isHeader,
      specialOrder: specialOrder || 999,
    };
  });

  // Separate header columns and stat columns
  const headerColumns = columnsWithMeta
    .filter((col) => col.isHeader)
    .sort((a, b) => a.specialOrder - b.specialOrder);

  // Get other columns and sort by seqNo
  const otherColumns = columnsWithMeta
    .filter((col) => !col.isHeader)
    .sort((a, b) => a.seqNo - b.seqNo);

  // Return the column definitions in the right order
  return [
    ...headerColumns.map((col) => col.columnDef),
    ...otherColumns.map((col) => col.columnDef),
  ];
};

// transformGameLogStats and memoize results
const transformGameLogStats = (stats, type) => {
  if (!stats?.length) return [];

  return stats
    .flatMap((seasonData) => {
      const statArray = seasonData[type];
      if (!statArray?.length) return [];

      return statArray
        .map((weekStat) => ({
          seasonId: seasonData.seasonId,
          weekNumber: weekStat.weekNumber || '',
          gameId: weekStat.gameId || '',
          homeTeamName: weekStat.homeTeamName || '',
          awayTeamName: weekStat.awayTeamName || '',
          ...Object.fromEntries(
            Object.entries(weekStat || {}).map(([key, value]) => [key, value]),
          ),
        }))
        .filter(
          (stat) =>
            stat.weekNumber != null &&
            stat.gameId != null &&
            stat.gameDate != null &&
            stat.statType === 'Game Stats',
        );
    })
    .filter(Boolean);
};

// Custom sorting function
const customSortingFn = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  // Special handling for gameDate
  if (columnId === 'gameDate') {
    const dateA = a ? new Date(a) : new Date(0);
    const dateB = b ? new Date(b) : new Date(0);
    return dateA - dateB; // Reverse chronological order
  }

  // Special handling for GP-GS
  if (columnId === 'gamesPlayedStarted') {
    return b - a; // Reverse numerical order
  }

  // Handle numeric values
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  // Handle string values
  return (a ?? '').toString().localeCompare((b ?? '').toString());
};

// StatsButton component
const StatsButton = ({ type, activeTable, onClick }) => (
  <div className="flex items-center justify-center gap-1">
    <button
      data-target={type}
      className={clsx(
        'rounded text-center font-nova text-xl font-bold uppercase leading-tight tracking-[1.5px] underline-offset-4 lg:border lg:border-[#ababab] lg:px-6 lg:py-4',
        activeTable === GAME_LOG_STATS_CONFIG[type].id
          ? 'text-black underline lg:bg-black lg:text-white lg:no-underline'
          : 'text-[#ababab]',
      )}
      onClick={() => onClick(type)}
    >
      {GAME_LOG_STATS_CONFIG[type].label}
    </button>
  </div>
);

// Table component
const StatsTable = ({ table, isVisible }) => (
  <div
    className={clsx(
      'relative max-h-[calc(5*4.5rem)] overflow-x-auto lg:max-h-[calc(9*4.5rem)]',
      !isVisible && 'hidden',
    )}
  >
    <table className="-my-1 min-w-full border-separate border-spacing-y-1 text-left text-[18px] font-semibold leading-9 tracking-[1.5px]">
      <thead className="box-s sticky top-0 z-1 bg-[--theme-secondary]">
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className={clsx(
                  'cursor-pointer select-none whitespace-nowrap px-3 py-3 font-nova text-lg font-semibold uppercase leading-tight tracking-wider text-[--theme-action]',
                  header.column.columnDef.header === 'Date' &&
                    'sticky left-0 z-10 bg-[--theme-secondary]',
                )}
                onClick={header.column.getToggleSortingHandler()}
              >
                <div className="flex items-center gap-x-2">
                  {header.column.columnDef.header}
                  <CaretIcon
                    className={clsx(
                      'w-3',
                      !header.column.getIsSorted()
                        ? 'invisible'
                        : header.column.getIsSorted() === 'asc'
                          ? 'rotate-180'
                          : null,
                    )}
                  />
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr
            key={row.id}
            className="cursor-pointer border-b-2 border-white bg-[#f0f0f0]"
          >
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                className={clsx(
                  'whitespace-nowrap px-3 py-3 font-nova text-lg font-semibold leading-tight tracking-wider text-black',
                  cell.column.columnDef.header === 'Date' &&
                    'sticky left-0 bg-[#f0f0f0]',
                )}
              >
                {cell.column.columnDef.cell(cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Main component
export function PlayerGameLogTable({ data, seasons }) {
  // Transform the stats
  const stats = useMemo(
    () => ({
      battingStats: transformGameLogStats(data, 'battingStats'),
      fieldingStats: transformGameLogStats(data, 'fieldingStats'),
      pitchingStats: transformGameLogStats(data, 'pitchingStats'),
    }),
    [data],
  );

  // Initialize state first
  const [sorting, setSorting] = useState([{ id: 'gameDate', desc: true }]);
  const [selectedYear, setSelectedYear] = useState('');

  // Get available stats after selectedYear is initialized
  const availableStats = useMemo(
    () =>
      Object.keys(GAME_LOG_STATS_CONFIG).filter((type) => {
        const statsForType = stats[GAME_LOG_STATS_CONFIG[type].id];
        if (!statsForType?.length) return false;

        // If a year is selected, check if there's data for that year
        if (selectedYear) {
          return statsForType.some(
            (stat) => stat.seasonId === Number(selectedYear),
          );
        }
        return true;
      }),
    [stats, selectedYear],
  );

  // Get unique years from the data and match with seasons description
  const yearOptions = useMemo(() => {
    const uniqueSeasonIds = new Set();
    Object.values(stats).forEach((statType) => {
      statType.forEach((stat) => {
        if (stat?.seasonId) {
          uniqueSeasonIds.add(stat.seasonId);
        }
      });
    });

    return Array.from(uniqueSeasonIds)
      .map((seasonId) => ({
        value: seasonId.toString(), // Convert to string consistently
        label:
          seasons.find((s) => s.seasonId === seasonId)?.description ||
          `Season ${seasonId}`,
      }))
      .sort((a, b) => Number(b.value) - Number(a.value));
  }, [stats, seasons]);

  // Get initial active table based on position and available stats
  const initialActiveTable = useMemo(() => {
    // If player is a pitcher, show pitching stats first if available
    if (
      data[0]?.primaryPosition?.positionLk === 'RHP' ||
      data[0]?.primaryPosition?.positionLk === 'LHP'
    ) {
      if (availableStats.includes('pitching')) {
        return GAME_LOG_STATS_CONFIG.pitching.id;
      }
    }

    // Otherwise use the first available stat type
    const firstAvailableStat = availableStats[0];
    return firstAvailableStat
      ? GAME_LOG_STATS_CONFIG[firstAvailableStat].id
      : '';
  }, [availableStats, data]);

  const [activeTable, setActiveTable] = useState(initialActiveTable);

  // Memoize active configuration with year filtering
  const activeConfig = useMemo(() => {
    const type = Object.values(GAME_LOG_STATS_CONFIG).find(
      (config) => config.id === activeTable,
    );

    if (!type) {
      return {
        data: [],
        columns: [],
      };
    }

    let filteredData = stats[activeTable];
    if (selectedYear) {
      filteredData = filteredData.filter(
        (row) => row.seasonId === Number(selectedYear),
      );
    }

    // If there's no data for the current active table after filtering,
    // find the first available stat type that has data
    if (!filteredData?.length && availableStats.length > 0) {
      const firstAvailableType = availableStats[0];
      const newType = Object.values(GAME_LOG_STATS_CONFIG).find(
        (config) => config.id === GAME_LOG_STATS_CONFIG[firstAvailableType].id,
      );

      // Update active table state
      setActiveTable(GAME_LOG_STATS_CONFIG[firstAvailableType].id);

      // Get filtered data for the new type
      filteredData = stats[GAME_LOG_STATS_CONFIG[firstAvailableType].id];
      if (selectedYear) {
        filteredData = filteredData.filter(
          (row) => row.seasonId === Number(selectedYear),
        );
      }

      // Add statTableId to columns that don't have one
      const columnsWithTableId = newType.columns.map((col) => ({
        ...col,
        statTableId:
          col.statTableId !== undefined ? col.statTableId : newType.tableId,
      }));

      return {
        data: filteredData,
        columns: createColumns(columnsWithTableId, statsMetaData),
      };
    }

    // Add statTableId to columns that don't have one
    const columnsWithTableId = type.columns.map((col) => ({
      ...col,
      statTableId:
        col.statTableId !== undefined ? col.statTableId : type.tableId,
    }));

    return {
      data: filteredData,
      columns: createColumns(columnsWithTableId, statsMetaData),
    };
  }, [activeTable, stats, selectedYear, availableStats]);

  // Memoize table instance
  const table = useReactTable({
    data: activeConfig.data,
    columns: activeConfig.columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
  });

  // Memoize button click handler
  const handleButtonClick = useCallback((type) => {
    setActiveTable(GAME_LOG_STATS_CONFIG[type].id);
    setSorting([{ id: 'gameDate', desc: true }]);
  }, []);

  return (
    <Container className="relative gap-6 py-5 lg:my-16">
      <div className="filter-wrap flex-row items-end justify-between gap-x-6 lg:flex">
        <div className="filter-title flex w-full flex-col justify-between lg:flex-row lg:items-center">
          <h2 className="shrink grow basis-0 font-nova text-4xl font-black uppercase text-black">
            Game Log
          </h2>
        </div>
      </div>

      <div className="mr-auto flex w-full flex-wrap items-center gap-x-6 lg:gap-1">
        {availableStats.map((type) => (
          <StatsButton
            key={type}
            type={type}
            activeTable={activeTable}
            onClick={handleButtonClick}
          />
        ))}
        {/* Set initial selected year to latest year when yearOptions changes */}
        {useMemo(() => {
          if (yearOptions.length > 0 && !selectedYear) {
            setSelectedYear(yearOptions[0].value);
          }
        }, [yearOptions, selectedYear])}

        <CustomDropdown
          options={yearOptions}
          label="Year"
          value={selectedYear}
          onChange={setSelectedYear}
          allLabel=""
          className="ml-auto"
          collapsible
        />
      </div>

      <StatsTable table={table} isVisible={true} />
      <p className="text-gray-500 text-sm">
        *The AUSL All-Star Cup was known as the Championship season through
        2024.
      </p>
    </Container>
  );
}
